import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { CultureService } from '@app/core/services/system-language/culture.service';
import { Culture } from '@app/shared/models/system-language/culture.model';
import { IntegrationsService } from '@app/modules/site-settings-integrations/services/integrations.service';
import { AdpIntegrationDetails } from '@app/modules/site-settings-integrations/models/integrations.model';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { OrganizationStructureService } from '@app/modules/organization-structure/services/organization-structure.service';
import { Organization } from '@app/modules/organization-structure/models/organization-structure.model';
import { TranslateService } from '@ngx-translate/core';
import { FilterExpandSettings } from '@progress/kendo-angular-treeview';
import { finalize, from } from 'rxjs';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CERTIFICATE_CRITICAL_DAYS = 30;
const CERTIFICATE_WARNING_DAYS = 90;

export type CertificateExpiryStatus = 'ok' | 'warning' | 'critical';

@Component({
  selector: 'app-adp-details-tab',
  templateUrl: './adp-details-tab.component.html',
  styleUrls: ['./adp-details-tab.component.scss']
})
export class AdpDetailsTabComponent implements OnInit {
  @Input() integrationId: string;

  public integrationConfig: AdpIntegrationDetails = null;
  public cultures: Culture[] = [];
  public form: any;
  public isLoading: boolean = false;
  public selectedCertificate: File = null;

  public rotateCertificate: File = null;
  public rotateCertificatePassword: string = null;

  public certificateExpiresOn: Date = null;
  public certificateDaysRemaining: number = null;
  public certificateExpired: boolean = false;
  public certificateExpiryStatus: CertificateExpiryStatus = 'ok';
  public certificateExpiryIcon: string = 'check_circle';

  public organizationTree: any[] = [];
  public organizationFilterExpandSettings: FilterExpandSettings = {
    expandMatches: true
  };

  constructor(
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private cultureService: CultureService,
    private integrationService: IntegrationsService,
    private organizationService: OrganizationStructureService,
  ) {
    this.createForm();
  }

  ngOnInit(): void {
    this.cultureService.getCultures().subscribe(res => {
      this.cultures = res;
    });

    from(this.organizationService.getAllOrganizations()).subscribe(res => {
      this.organizationTree = this.convertListToTreeViewData(res.data);
    });

    this.getData();
  }

  // The certificate is only entered once; after that it's replaced through Update Certificate.
  get hasCertificate(): boolean {
    return !!this.integrationConfig?.hasCertificate;
  }

  createForm() {
    this.form = this.fb.group({
      clientId: [null, Validators.required],
      clientSecret: [null, Validators.required],
      certificatePassword: [null],
      defaultOrganization: [null, Validators.required],
      authUrl: [null, Validators.required],
      baseUrl: [null, Validators.required],
      cultureId: [null, Validators.required],
    });
  }

  getData() {
    this.isLoading = true;

    this.integrationService.getAdpIntegrationConfig(this.integrationId)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(res => {
        this.integrationConfig = res;
        this.setCertificateExpiry(res.certificateExpiresOn);

        // The stored secret is never returned, so once one exists a blank field means "keep it".
        const clientSecret = this.form.get('clientSecret');
        clientSecret.setValidators(res.hasClientSecret ? null : Validators.required);
        clientSecret.updateValueAndValidity();

        this.form.patchValue({
          clientId: res.clientId,
          defaultOrganization: res.defaultOrganization ?? null,
          authUrl: res.authUrl || 'https://accounts.adp.com',
          baseUrl: res.baseUrl || 'https://api.adp.com',
          cultureId: res.culture ? res.culture.id : null,
        });
      });
  }

  private convertListToTreeViewData(orgs: Organization[]) {
    orgs.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    return orgs.map(org => ({
      id: org.id,
      name: org.name,
      parent: org.parentOrganization?.id,
      type: org.organizationType?.name
    }));
  }

  private setCertificateExpiry(expiresOn: string | null) {
    // A certificate is on file but its expiry couldn't be read - flagged red like an expired one.
    if (!expiresOn) {
      this.certificateExpiresOn = null;
      this.certificateDaysRemaining = null;
      this.certificateExpired = false;
      this.certificateExpiryStatus = 'critical';
      this.certificateExpiryIcon = 'error';
      return;
    }

    this.certificateExpiresOn = new Date(expiresOn);

    const msRemaining = this.certificateExpiresOn.getTime() - Date.now();
    this.certificateExpired = msRemaining <= 0;
    this.certificateDaysRemaining = Math.max(0, Math.ceil(msRemaining / MS_PER_DAY));

    if (this.certificateDaysRemaining <= CERTIFICATE_CRITICAL_DAYS) {
      this.certificateExpiryStatus = 'critical';
      this.certificateExpiryIcon = 'error';
    } else if (this.certificateDaysRemaining <= CERTIFICATE_WARNING_DAYS) {
      this.certificateExpiryStatus = 'warning';
      this.certificateExpiryIcon = 'warning';
    } else {
      this.certificateExpiryStatus = 'ok';
      this.certificateExpiryIcon = 'check_circle';
    }
  }

  onCertificateSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectedCertificate = input.files && input.files.length > 0 ? input.files[0] : null;
  }

  onRotateCertificateSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.rotateCertificate = input.files && input.files.length > 0 ? input.files[0] : null;
  }

  updateCertificate() {
    this.overlayService.show();

    const formData = new FormData();
    formData.append('Certificate', this.rotateCertificate);
    formData.append('CertificatePassword', this.rotateCertificatePassword ?? '');

    this.integrationService.updateAdpCertificate(this.integrationId, formData)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe({
        next: () => {
          this.snackbarService.openSnackBar(this.translate.instant('IntegrationConfigUpdated'), 'clear', 'success');
          this.rotateCertificate = null;
          this.rotateCertificatePassword = null;
          this.getData();
        }
      });
  }

  save() {
    this.overlayService.show();

    const formData = new FormData();
    const value = this.form.value;

    formData.append('ClientId', value.clientId ?? '');
    formData.append('DefaultOrganization', value.defaultOrganization?.id ?? '');
    formData.append('AuthUrl', value.authUrl ?? '');
    formData.append('BaseUrl', value.baseUrl ?? '');
    formData.append('CultureId', value.cultureId ?? '');

    // Left out when blank so the server keeps the stored secret / certificate.
    if (value.clientSecret) {
      formData.append('ClientSecret', value.clientSecret);
    }

    if (this.selectedCertificate) {
      formData.append('Certificate', this.selectedCertificate);
      formData.append('CertificatePassword', value.certificatePassword ?? '');
    }

    this.integrationService.updateAdpIntegrationConfig(this.integrationId, formData)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe({
        next: () => {
          this.snackbarService.openSnackBar(this.translate.instant('IntegrationConfigUpdated'), 'clear', 'success');
          this.selectedCertificate = null;
          this.form.patchValue({ clientSecret: null, certificatePassword: null });
          this.getData();
        }
      });
  }
}
